import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Users } from "@app/shared/src/api/api.gen";
import { AcceptedTerms, IAcceptedTerms, INewUserWithOrganization, NewUserWithOrganization } from "@app/shared/src/models";
import { Btn, TextInput } from "@app/shared/src/components_v2";
import { emptyOrganizationDraft, organizationDraftError } from "../../helpers/organizationDraft";
import { LATEST_TERMS_FILE } from "../../helpers/terms";
import { AuthFrame, AuthLabel, AuthLinks, AuthPanel } from "../layouts/AuthFrame";
import { useSession } from "../providers/SessionProvider";
import { OrganizationFields } from "../shared/OrganizationFields";

/**
 * A new director, and the organization they run.
 *
 * One request creates both and makes the person the organization's administrator — the same
 * endpoint the main dashboard's sign-up uses, so an account made here works there too. Signing in
 * follows straight away; if that step fails for any reason the account still exists, so the page
 * sends them to sign in rather than asking them to create it twice.
 *
 * Accepting the terms is part of creating the account: the checkbox is required, and the
 * acceptance is recorded against the new user once they are signed in.
 */

const MIN_PASSWORD = 8;

export const CreateAccountPage: React.FC = () => {
    const { user, isChecking, login, refresh } = useSession();
    const navigate = useNavigate();
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [org, setOrg] = useState(emptyOrganizationDraft);
    const [agreed, setAgreed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();

    if (!isChecking && user) return <Navigate to="/overview" replace />;

    const problem = (): string | undefined => {
        if (!firstName.trim() || !lastName.trim()) return "Enter your first and last name.";
        if (!email.trim()) return "Enter your email address.";
        if (password.length < MIN_PASSWORD) return `Choose a password of at least ${MIN_PASSWORD} characters.`;
        if (password !== confirmPassword) return "The passwords don't match.";
        const orgProblem = organizationDraftError(org);
        if (orgProblem) return orgProblem;
        if (!agreed) return "You need to accept the Terms and Conditions to create an account.";
        return undefined;
    };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (busy) return;
        const p = problem();
        if (p) {
            setError(p);
            return;
        }
        setBusy(true);
        setError(undefined);
        const address = email.trim();
        let userId: string | undefined;
        try {
            userId = await Users.createUser(
                new NewUserWithOrganization({
                    firstName: firstName.trim(),
                    lastName: lastName.trim(),
                    emailAddress: address,
                    password,
                    confirmPassword,
                    organizationName: org.name.trim(),
                    organizationPhoneNumber: org.phoneNumber.trim() || undefined,
                    organizationAddress: org.address.trim() || undefined,
                    organizationAddress2: org.address2.trim() || undefined,
                    organizationCity: org.city.trim() || undefined,
                    organizationState: org.state.trim() || undefined,
                    organizationZipcode: org.zipcode.trim() || undefined,
                } as INewUserWithOrganization),
            );
        } catch (err) {
            setError(err instanceof Error && err.message ? err.message : "Could not create the account");
            setBusy(false);
            return;
        }

        try {
            await login(address, password);
        } catch {
            // The account exists; only the automatic sign-in failed. Let them sign in by hand.
            navigate("/login", { replace: true, state: { email: address, notice: "Your account is ready — sign in to continue." } });
            return;
        }

        try {
            await Users.acceptTerms(userId, new AcceptedTerms({ id: undefined, fileName: LATEST_TERMS_FILE, acceptedAt: undefined } as IAcceptedTerms));
            await refresh();
        } catch {
            // Not worth blocking a brand-new director over: the terms page offers to record it again.
        }
        navigate("/overview", { replace: true });
    };

    const label = (text: string) => <AuthLabel>{text}</AuthLabel>;

    return (
        <AuthFrame wide>
            <div className="mb-5">
                <h1 className="text-[22px] font-bold tracking-tight">Create a new account</h1>
                <p className="mt-1.5 max-w-[60ch] text-[13.5px] leading-relaxed text-[var(--muted)]">
                    You and your organization, in one go. If your company already uses Omnisight, ask one of its administrators to
                    invite you instead — that puts you in the existing organization rather than a new one.
                </p>
            </div>

            <AuthPanel onSubmit={submit} className="flex flex-col gap-5">
                <section className="flex flex-col gap-3">
                    <h2 className="text-[14px] font-semibold">About you</h2>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <span>
                            {label("First name")}
                            <TextInput size="lg" autoComplete="given-name" required value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full" />
                        </span>
                        <span>
                            {label("Last name")}
                            <TextInput size="lg" autoComplete="family-name" required value={lastName} onChange={(e) => setLastName(e.target.value)} className="w-full" />
                        </span>
                    </div>
                    <span>
                        {label("Email")}
                        <TextInput size="lg" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full" />
                    </span>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <span>
                            {label("Password")}
                            <TextInput
                                size="lg"
                                type="password"
                                autoComplete="new-password"
                                required
                                minLength={MIN_PASSWORD}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full"
                            />
                        </span>
                        <span>
                            {label("Confirm password")}
                            <TextInput
                                size="lg"
                                type="password"
                                autoComplete="new-password"
                                required
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full"
                            />
                        </span>
                    </div>
                </section>

                <section className="flex flex-col gap-3 border-t border-[var(--line)] pt-5">
                    <h2 className="text-[14px] font-semibold">Your organization</h2>
                    <OrganizationFields draft={org} onChange={setOrg} disabled={busy} />
                </section>

                <label className="flex items-start gap-2.5 border-t border-[var(--line)] pt-5 text-[13px] leading-relaxed">
                    <input
                        type="checkbox"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        className="mt-[3px] h-4 w-4 flex-none accent-[var(--accent)]"
                    />
                    <span>
                        I have read and agree to the{" "}
                        <Link to="/terms" target="_blank" rel="noreferrer" className="font-semibold text-[var(--accent)]">
                            Terms and Conditions
                        </Link>
                        .
                    </span>
                </label>

                {error && <p className="text-[12.5px] text-[var(--crit)]">{error}</p>}

                <Btn type="submit" size="lg" disabled={busy} className="w-full">
                    {busy ? "Creating your account…" : "Create account"}
                </Btn>
            </AuthPanel>

            <AuthLinks>
                <Link to="/login" className="hover:text-[var(--text)]">
                    Already have an account? Sign in
                </Link>
            </AuthLinks>
        </AuthFrame>
    );
};
